import React, { useState } from "react";
import { Activity, TrendingUp, AlertTriangle } from "lucide-react";

// Color follows the entity: tone (positive/neutral/tense) is the hue —
// a reserved status read, not a generic categorical series — and energy
// (low/medium/high) modulates that hue's intensity via alpha, the same
// hex+alpha-suffix convention already used throughout this file's siblings
// (e.g. `${T.cyan}18`). Two dimensions, one hue family per cell — never a
// second competing hue — so the strip reads as "how intense" within
// "what kind," not as an ambiguous blend.
const ENERGY_ALPHA = { low: "14", medium: "30", high: "55" };
const ENERGY_LABEL = { low: "Low", medium: "Medium", high: "High" };

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function EngagementHeatmap({ heatmap, T, avatarColors }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!heatmap) return null;

  const TONE_COLOR = { positive: T.green, neutral: T.muted, tense: T.red };
  const segments = heatmap.segments || [];

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>
        <Activity size={13} style={{ color: T.amber }} /> Engagement Heatmap
      </div>

      {/* Quick stats */}
      <div style={{ display: "flex", gap: 22, marginBottom: 12 }}>
        {heatmap.duration_minutes != null && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>
              {heatmap.duration_minutes}<span style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}> min</span>
            </div>
            <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>Duration</div>
          </div>
        )}
        {heatmap.turn_count != null && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{heatmap.turn_count}</div>
            <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>Speaker Turns</div>
          </div>
        )}
      </div>

      {heatmap.overall_health && (
        <p style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.6, margin: "0 0 14px" }}>{heatmap.overall_health}</p>
      )}

      {/* Chronological heatmap strip */}
      {segments.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 2 }}>
            {segments.map((s) => {
              const color = TONE_COLOR[s.tone] || T.muted;
              const alpha = ENERGY_ALPHA[s.energy] || ENERGY_ALPHA.medium;
              return (
                <div key={s.index}
                  onMouseEnter={() => setHoverIdx(s.index)}
                  onMouseLeave={() => setHoverIdx(null)}
                  style={{ flex: 1, height: 30, borderRadius: 6, position: "relative", cursor: "default",
                    background: `${color}${alpha}`, border: `1px solid ${color}55`,
                    transition: "transform 0.15s ease", transform: hoverIdx === s.index ? "scaleY(1.12)" : "scaleY(1)" }}>
                  {hoverIdx === s.index && (
                    <div style={{ position: "absolute", bottom: "130%", left: "50%", transform: "translateX(-50%)",
                      background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 11px",
                      whiteSpace: "nowrap", zIndex: 20, boxShadow: T.shadow, pointerEvents: "none" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: T.text, marginBottom: 2 }}>{s.label}</div>
                      <div style={{ fontSize: 10.5, color: T.muted }}>
                        {fmtTime(s.start_time)} · {ENERGY_LABEL[s.energy] || s.energy} energy · <span style={{ textTransform: "capitalize" }}>{s.tone}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Legend — identity is never color-alone */}
          <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
            {Object.entries(TONE_COLOR).map(([tone, color]) => (
              <div key={tone} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: color, display: "inline-block" }} />
                <span style={{ fontSize: 10.5, color: T.muted, textTransform: "capitalize" }}>{tone}</span>
              </div>
            ))}
            <span style={{ fontSize: 10.5, color: T.dim }}>· fill intensity = energy</span>
          </div>
        </div>
      )}

      {/* Talk-time distribution */}
      {heatmap.speaker_talk_time?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 9 }}>
            Talk Time
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {heatmap.speaker_talk_time.map((sp, i) => (
              <div key={sp.speaker}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 3 }}>
                  <span style={{ color: T.text, fontWeight: 600 }}>{sp.speaker}</span>
                  <span style={{ color: T.muted }}>{sp.pct}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 4, background: T.card2, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${sp.pct}%`, borderRadius: 4,
                    background: avatarColors[i % avatarColors.length], transition: "width 0.4s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Peak / friction callouts */}
      {(heatmap.peak_moments?.length > 0 || heatmap.friction_moments?.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {heatmap.peak_moments?.map((m, i) => (
            <div key={`p${i}`} style={{ display: "flex", gap: 8, padding: "8px 10px", borderRadius: 8,
              background: `${T.green}0F`, borderLeft: `3px solid ${T.green}` }}>
              <TrendingUp size={13} style={{ color: T.green, flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: T.green, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Peak{fmtTime(m.time) ? ` · ${fmtTime(m.time)}` : ""}
                </div>
                <div style={{ fontSize: 12, color: T.text, marginTop: 2, lineHeight: 1.5 }}>{m.description}</div>
              </div>
            </div>
          ))}
          {heatmap.friction_moments?.map((m, i) => (
            <div key={`f${i}`} style={{ display: "flex", gap: 8, padding: "8px 10px", borderRadius: 8,
              background: `${T.red}0F`, borderLeft: `3px solid ${T.red}` }}>
              <AlertTriangle size={13} style={{ color: T.red, flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: T.red, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Friction{fmtTime(m.time) ? ` · ${fmtTime(m.time)}` : ""}
                </div>
                <div style={{ fontSize: 12, color: T.text, marginTop: 2, lineHeight: 1.5 }}>{m.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
